import React, { useMemo } from 'react';
import { AimIconButton } from '../../../../../../commons/components/AimButtons';

const LAMBDA = () => {};

const ACTIONS_STATE = {
	EDIT_DELETE: 'EDIT_DELETE',
	DONE: 'DONE',
	DONE_CANCEL: 'DONE_CANCEL',
	EDIT_DELETE_UNDO: 'EDIT_DELETE_UNDO',
	UNDODELETE: 'UNDODELETE',
	HIDDEN: 'HIDDEN'
};

export const computeActionsState = ({
	hasSelectedEntity,
	isSelected,
	isActive,
	hasNameChange
} = {}) => {
	if (hasSelectedEntity && isSelected) {
		if (hasNameChange) {
			return ACTIONS_STATE.DONE_CANCEL;
		}
		return ACTIONS_STATE.DONE;
	}

	if (!isActive) {
		return ACTIONS_STATE.UNDODELETE;
	}

	if (hasNameChange) {
		return ACTIONS_STATE.EDIT_DELETE_UNDO;
	}

	return ACTIONS_STATE.EDIT_DELETE;
};

const computeButtonStyle = (
	bsStyle,
	{ hasSelectedEntity, isSelected, isDelete, isAssignedToJobs } = {}
) => {
	if ((hasSelectedEntity && !isSelected) || (isDelete && isAssignedToJobs)) {
		return 'default';
	}
	return bsStyle;
};

export const BLOCKER = ({ hasSelectedEntity, isSelected } = {}) => {
	const blockActions = useMemo(() => hasSelectedEntity && !isSelected, [
		hasSelectedEntity,
		isSelected
	]);
	return blockActions ? <div className="entity-card-action-blocker" /> : null;
};

const BUTTONUNDO = ({ isNew, ...remainingProps }) => {
	return !isNew ? <AimIconButton {...remainingProps} /> : null;
};

export const ACTIONS = ({
	actionsState,
	hasSelectedEntity,
  isSelected,
  origName,
	isNew,
  hasNameConflict,
  revertHasNameConflict,
	isAssignedToJobs,
	onClickEdit = LAMBDA,
	onClickDelete = LAMBDA,
	onClickCancel = LAMBDA,
	onClickUndo = LAMBDA,
	onClickDone = LAMBDA
}) => {
	switch (actionsState) {
		case ACTIONS_STATE.EDIT_DELETE:
			return (
				<div className="col-grow col-right col-actions">
					<AimIconButton
						bsStyle={computeButtonStyle('success', {
							hasSelectedEntity,
							isSelected
						})}
						className="outline material"
						iconClassname="fa fa-pencil"
						onClick={onClickEdit}
						title="Edit"
					/>
					<AimIconButton
						bsStyle={computeButtonStyle('danger', {
							hasSelectedEntity,
							isSelected,
							isDelete: true,
							isAssignedToJobs
						})}
						className="outline material"
						iconClassname="fa fa-trash"
						onClick={onClickDelete}
						title={
							!isAssignedToJobs
								? 'Delete'
								: 'Button is Disabled: this entity is assigned to jobs in scanline.'
						}
						disabled={isAssignedToJobs}
					/>
					<BLOCKER
						hasSelectedEntity={hasSelectedEntity}
						isSelected={isSelected}
					/>
				</div>
			);
		case ACTIONS_STATE.DONE:
			return (
				<div className="col-grow col-right col-actions">
					<AimIconButton
						bsStyle={computeButtonStyle('success', {
							hasSelectedEntity,
							isSelected
						})}
						className="outline material"
						iconClassname="fa fa-check"
						onClick={onClickDone}
						title={
							!hasNameConflict ? 'Done' : 'Disabled: the name already exists'
						}
						disabled={hasNameConflict}
					/>
					<BLOCKER
						hasSelectedEntity={hasSelectedEntity}
						isSelected={isSelected}
					/>
				</div>
			);
		case ACTIONS_STATE.DONE_CANCEL:
			return (
				<div className="col-grow col-right col-actions">
					<AimIconButton
						bsStyle={computeButtonStyle('success', {
							hasSelectedEntity,
							isSelected
						})}
						className="outline material"
						iconClassname="fa fa-check"
						onClick={onClickDone}
						title={
							!hasNameConflict
								? 'Keep Changes'
								: 'Disabled: the name already exists'
						}
						disabled={hasNameConflict}
					/>
					<BUTTONUNDO
						bsStyle={computeButtonStyle('default', {
							hasSelectedEntity,
							isSelected
						})}
						className="outline material"
						iconClassname="fa fa-undo"
            onClick={onClickCancel}
            disabled={revertHasNameConflict}
						title={
							!revertHasNameConflict
								? 'Undo All Changes'
								: `Disabled: There is an active entity with the name: "${origName}".`
						}
						isNew={isNew}
					/>
					<BLOCKER
						hasSelectedEntity={hasSelectedEntity}
						isSelected={isSelected}
					/>
				</div>
			);
		case ACTIONS_STATE.EDIT_DELETE_UNDO:
			return (
				<div className="col-grow col-right col-actions">
					<AimIconButton
						bsStyle={computeButtonStyle('success', {
							hasSelectedEntity,
							isSelected
						})}
						className="outline material"
						iconClassname="fa fa-pencil"
						onClick={onClickEdit}
						title="Edit"
					/>
					<AimIconButton
						bsStyle={computeButtonStyle('danger', {
							hasSelectedEntity,
							isSelected,
							isDelete: true,
							isAssignedToJobs
						})}
						className="outline material"
						iconClassname="fa fa-trash"
						onClick={onClickDelete}
						title={
							!isAssignedToJobs
								? 'Delete'
								: 'Button is Disabled: this entity is assigned to jobs in scanline.'
						}
						disabled={isAssignedToJobs}
					/>
					<BUTTONUNDO
						bsStyle={computeButtonStyle('default', {
							hasSelectedEntity,
							isSelected
						})}
						className="outline material"
						iconClassname="fa fa-undo"
            onClick={onClickUndo}
            disabled={revertHasNameConflict}
						title={
							!revertHasNameConflict
								? 'Undo All Changes'
								: `Disabled: There is an active entity with the name: "${origName}".`
						}
						isNew={isNew}
					/>
					<BLOCKER
						hasSelectedEntity={hasSelectedEntity}
						isSelected={isSelected}
					/>
				</div>
			);
		case ACTIONS_STATE.UNDODELETE:
			return (
				<div className="col-grow col-right col-actions">
					<BUTTONUNDO
						bsStyle={computeButtonStyle('default', {
							hasSelectedEntity,
							isSelected
						})}
						className="outline material"
						iconClassname="fa fa-undo"
						onClick={onClickUndo}
            disabled={revertHasNameConflict}
						title={
							!revertHasNameConflict
								? 'Undo All Changes'
								: `Disabled: There is an active entity with the name: "${origName}".`
						}
						isNew={isNew}
					/>
					<BLOCKER
						hasSelectedEntity={hasSelectedEntity}
						isSelected={isSelected}
					/>
				</div>
			);
		case ACTIONS_STATE.HIDDEN:
			return null;
		default:
			return null;
	}
};
